import { CanceledError } from 'axios';
import { defineStore, type StateTree } from 'pinia';
import { useApi } from '@/services/api/useApi';
import { VGL_ERROR_CODE } from '@/services/errorHandler/errorCodes';
import { getErrorMessage } from '@/services/errorHandler/utils';
import { VglError } from '@/services/errorHandler/vglError';
import { parseCompanyQuestions } from '@/features/CompanyQuestions/parseCompanyQuestions';
import { useContractStore, useInputStore } from '@/features/Sparten';
import { useTariffStore } from '@/features/Tariffs/store';
import type { CompanyQuestionGroup } from '@/features/CompanyQuestions/types';
import { COMPANY_QUESTION_COMPONENT } from '@/features/CompanyQuestions/types';
import * as yup from 'yup';
import cloneDeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';
import { useQuoteStore } from '@/features/Quotes/store';
import { useStorableState } from '@/utils/useStorableState/useStorableState';

interface CompanyQuestionStore {
  data: CompanyQuestionGroup[];
  inputState: Record<string, unknown>;
  mergedForeignStoreStateClone: StateTree;
  forms: Set<string>;
  roleRelatedForms: Set<string>;
  flatValidationInputData: Record<string, unknown>;
  isLoading: boolean;
  labelByPath: Map<string, string>;
}

export const useCompanyQuestionStore = defineStore('CompanyQuestionStore', {
  state: (): CompanyQuestionStore => ({
    isLoading: false,
    data: [],
    inputState: {},
    mergedForeignStoreStateClone: {},
    forms: new Set(),
    roleRelatedForms: new Set(),
    flatValidationInputData: {},
    labelByPath: new Map(),
  }),

  actions: {
    async load(insuranceId: string) {
      try {
        const api = useApi().companies;

        this.isLoading = true;
        const [, request] = api.loadQuestions(insuranceId);

        this.forms.clear();

        this.mergedForeignStoreStateClone = merge(
          cloneDeep(useContractStore().$state),
          cloneDeep(useInputStore().$state),
          cloneDeep(useTariffStore().contractTariff),
        );
        // after we parsed the questions, this state will be extended by the question input state
        this.flatValidationInputData = this.mergedForeignStoreStateClone;

        const questionData = await request;

        const parsedQuestions = parseCompanyQuestions(questionData);

        const inputState: Record<string, string | number | undefined> = {};
        parsedQuestions.forEach((group) => {
          group.questions.forEach((q) => {
            inputState[q.fieldName] = q.default;
            this.labelByPath.set(q.fieldName, q.label);

            if (
              q.default === '0' &&
              q.component === COMPANY_QUESTION_COMPONENT.TEXT
            ) {
              inputState[q.fieldName] = '';
            }

            if (q.default === undefined || q.default === '') {
              if (
                q.component === COMPANY_QUESTION_COMPONENT.RADIO &&
                q.radioOptions.length > 0
              ) {
                inputState[q.fieldName] = q.radioOptions[0].value;
              }
            }
          });
        });

        this.data = parsedQuestions;
        const optionalQuoteContent = useQuoteStore().data?.content;
        const companyQuestionData = useStorableState().read(
          optionalQuoteContent?.companyQuestionData,
        );

        this.inputState = {
          ...inputState,
          ...companyQuestionData,
        };

        this.flatValidationInputData = merge(
          this.mergedForeignStoreStateClone,
          cloneDeep(this.inputState),
        );
      } catch (error) {
        if (!(error instanceof CanceledError)) {
          throw new VglError(
            VGL_ERROR_CODE.COMPANY_QUESTION_FAILED_TO_LOAD_QUESTIONS,
            {
              additionalData: {
                insuranceId,
              },
              additionalErrorMessage: `Error: ${getErrorMessage(error)}`,
              cause: error,
            },
          );
        }

        throw error;
      } finally {
        this.isLoading = false;
      }
    },

    getInputSchema(): yup.AnySchema {
      let schema: yup.AnySchema = yup.object({});
      this.data.forEach((group) => {
        group.questions.forEach((q) => {
          if (q.requirements()) {
            schema = schema.concat(q.schema as yup.AnySchema);
          }
        });
      });

      return schema;
    },

    getDehydratedInputState() {
      return Object.fromEntries(
        Object.entries(this.inputState).filter(([key]) =>
          this.relevantQuestionPaths.includes(key),
        ),
      );
    },
  },

  getters: {
    hasQuestions: (state) => Object.keys(state.inputState).length > 0,
    relevantQuestionPaths: (state) => {
      const relevantPaths: string[] = [];
      state.data.forEach((group) => {
        group.questions.forEach((question) => {
          if (question.requirements()) {
            relevantPaths.push(question.fieldName);
          }
        });
      });

      return relevantPaths;
    },
  },
});
